import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { categories, categoryMap } from '@/mocks/categories';
import { articles, getArticlesByCategory } from '@/mocks/articles';
import Breadcrumb from '@/components/base/Breadcrumb';

function SeoHead({
  title,
  description,
  keywords,
  canonicalUrl,
  jsonLd,
}: {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  jsonLd?: Record<string, unknown>;
}) {
  useEffect(() => {
    document.title = title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', description);
    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', keywords);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', canonicalUrl);
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', title);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', description);
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', canonicalUrl);
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) twitterTitle.setAttribute('content', title);
    const twitterDesc = document.querySelector('meta[name="twitter:description"]');
    if (twitterDesc) twitterDesc.setAttribute('content', description);

    if (jsonLd) {
      const existingScript = document.getElementById('page-jsonld');
      if (existingScript) existingScript.remove();
      const script = document.createElement('script');
      script.id = 'page-jsonld';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, keywords, canonicalUrl, jsonLd]);

  return null;
}

function ArticleCard({ article }: { article: (typeof articles)[0] }) {
  return (
    <Link
      to={`/article/${article.slug}`}
      className="group flex flex-col sm:flex-row gap-5 rounded-2xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 hover:-translate-y-1 overflow-hidden cursor-pointer"
    >
      <div className="sm:w-64 aspect-[16/10] sm:aspect-auto overflow-hidden shrink-0">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          title={`${article.title} - 问鼎.cc`}
        />
      </div>
      <div className="p-5 sm:py-5 sm:pr-5 sm:pl-0 flex-1 flex flex-col justify-center min-w-0">
        <div className="flex items-center gap-3 mb-2 flex-wrap">
          <span className="text-xs text-foreground-400">{article.date}</span>
          <span className="text-xs text-foreground-400">{article.readTime}</span>
        </div>
        <h3 className="text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors duration-200 font-heading line-clamp-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed">
          {article.excerpt}
        </p>
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          {article.tags.map((tag) => (
            <span key={tag} className="text-xs text-primary-500 bg-primary-50 px-2 py-0.5 rounded-full whitespace-nowrap">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const currentCategory = slug ? categoryMap[slug] : undefined;

  const filteredArticles = slug ? getArticlesByCategory(slug) : [];

  const pageTitle = currentCategory
    ? `${currentCategory.name} - 问鼎.cc`
    : '分类 - 问鼎.cc';
  const pageDesc = currentCategory
    ? `浏览问鼎.cc 的${currentCategory.name}内容，${currentCategory.description}。涵盖最新资讯、深度分析和专业指南。`
    : '浏览问鼎.cc 全部分类内容。';

  const categoryJsonLd = currentCategory
    ? {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `${currentCategory.name} - 问鼎.cc`,
        description: pageDesc,
        url: `https://hecaoqing.com.cn/category/${slug}`,
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hecaoqing.com.cn/' },
            { '@type': 'ListItem', position: 2, name: currentCategory.name, item: `https://hecaoqing.com.cn/category/${slug}` },
          ],
        },
      }
    : undefined;

  return (
    <>
      <SeoHead
        title={pageTitle}
        description={pageDesc}
        keywords={`问鼎.cc,${currentCategory?.name || '分类'},问鼎cc官网`}
        canonicalUrl={`https://hecaoqing.com.cn/category/${slug || ''}`}
        jsonLd={categoryJsonLd}
      />

      <main className="pt-16 min-h-screen bg-background-100">
        {/* Category Header */}
        <section className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-6 py-12 md:py-16">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: currentCategory?.name || '分类' },
              ]}
            />
            <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mt-2">
              {currentCategory?.name || '全部分类'}
            </h1>
            {currentCategory && (
              <p className="text-sm text-foreground-500 mt-3 max-w-2xl leading-relaxed">
                {currentCategory.description}
              </p>
            )}
          </div>
        </section>

        {/* Category Tabs */}
        <div className="border-b border-background-200/70 bg-background-50 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center gap-0 overflow-x-auto scrollbar-hide -mb-px">
              <button
                onClick={() => navigate('/category/brand-news')}
                className={`px-5 py-3.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors cursor-pointer ${
                  slug === 'brand-news'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-foreground-500 hover:text-foreground-800'
                }`}
              >
                品牌动态
              </button>
              <button
                onClick={() => navigate('/category/industry-insight')}
                className={`px-5 py-3.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors cursor-pointer ${
                  slug === 'industry-insight'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-foreground-500 hover:text-foreground-800'
                }`}
              >
                行业洞察
              </button>
              <button
                onClick={() => navigate('/category/product-service')}
                className={`px-5 py-3.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors cursor-pointer ${
                  slug === 'product-service'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-foreground-500 hover:text-foreground-800'
                }`}
              >
                产品服务
              </button>
              <button
                onClick={() => navigate('/category/tech-frontier')}
                className={`px-5 py-3.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors cursor-pointer ${
                  slug === 'tech-frontier'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-foreground-500 hover:text-foreground-800'
                }`}
              >
                技术前沿
              </button>
              <button
                onClick={() => navigate('/category/customer-case')}
                className={`px-5 py-3.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors cursor-pointer ${
                  slug === 'customer-case'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-foreground-500 hover:text-foreground-800'
                }`}
              >
                客户案例
              </button>
            </div>
          </div>
        </div>

        {/* Articles List */}
        <section className="py-12 md:py-16">
          <div className="max-w-4xl mx-auto px-6">
            {filteredArticles.length > 0 ? (
              <div className="flex flex-col gap-5">
                {filteredArticles.map((article) => (
                  <ArticleCard key={article.slug} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-200 text-foreground-400">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}